// ADR-0014 §7.1 / §12 C-5, §12 D-2 — the tenant provisioning wizard's API client. Mirrors the routes
// `PlatformTenantEndpoints` maps under `/api/platform/tenants/{id}/provisioning`: one status read the wizard
// renders and reloads, and one write per step the operator can retry on its own.
//
// Step 1 is NOT here. It is `createTenant` in `./tenantsApi` — the wizard's first step drives the create
// endpoint that already exists, so there is one way to make a tenant and the wizard adds no second one.
//
// Every type comes from the generated contract (F-2); nothing here is hand-declared. That is no longer a
// contrast with `./tenantsApi`, which derives `Tenant` from `TenantDto` the same way: every operation on
// both clients declares its success body.

import type { components } from "@clms/contracts";
import { apiFetch } from "@web/lib/api";

/** Where a tenant's four provisioning steps stand (`TenantProvisioningDto`). */
export type TenantProvisioning = components["schemas"]["TenantProvisioningDto"];

/** One step: its code, its state, and the follow-up task that still owes it work. */
export type TenantProvisioningStep = components["schemas"]["TenantProvisioningStepDto"];

/** The identity bound as the tenant's first administrator — a subject and two dates, never a name. */
export type TenantFirstAdministrator = components["schemas"]["TenantFirstAdministratorDto"];

/** The invite's 200 body: the whole status after the step, plus whether the mail left. */
export type TenantFirstAdminInvite = components["schemas"]["TenantFirstAdminInviteDto"];

/** The cancel's 200 body: the status after the cancel, plus what became of the realm account. */
export type TenantFirstAdminCancel = components["schemas"]["TenantFirstAdminCancelDto"];

export type SetTenantEntitlementsRequest = components["schemas"]["SetTenantEntitlementsRequest"];
export type InviteFirstAdministratorRequest =
  components["schemas"]["InviteFirstAdministratorRequest"];

/**
 * The application entitlements step 3 can record, in the backend's own order
 * (`ApplicationEntitlements.All`, closed by `ck_tenants_entitlements_known`).
 *
 * Pinned in both locales by `src/i18n/tenant-provisioning-i18n.test.mjs`, which reads the C# source: a code
 * added there and not here is an application the operator cannot sell, and one added here and not there is
 * a checkbox whose save answers 422.
 */
export const APPLICATION_ENTITLEMENTS = ["clms", "hrm", "board"] as const;

export type ApplicationEntitlement = (typeof APPLICATION_ENTITLEMENTS)[number];

/** The i18n key naming an application. Unknown codes render a generic label rather than the raw code. */
export function entitlementLabelKey(code: string): string {
  return (APPLICATION_ENTITLEMENTS as readonly string[]).includes(code)
    ? `tenants.provisioning.entitlement.${code}`
    : "tenants.provisioning.entitlement.unknown";
}

/** The `mail` value of a successful invite whose message really left. */
export const MAIL_SENT = "sent";

/**
 * Read where a tenant's provisioning stands. 404 when there is no such customer tenant (the operator's own
 * home included — ADR-0014 A-2).
 *
 * This read is itself an audited cross-tenant access (`TENANT_ACCESSED`, `{"viewed":"tenant_provisioning"}`
 * — FR-Q.35), exactly like the pääkäyttäjyys candidate read, so it is called when the operator OPENS the
 * wizard and after each step — never to decorate the tenant list. The list's own "provisioning finished"
 * badge comes from `listTenants`, which discloses no customer data and is deliberately not audited.
 */
export function getProvisioning(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantProvisioning> {
  return apiFetch<TenantProvisioning>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/provisioning`,
    { signal },
  );
}

/**
 * The problem-details `error` value step 3's 409 carries: the entitlement set changed between the read this
 * request was built from and its write, so NOTHING was recorded
 * (`TenantProvisioningErrors.EntitlementsChangedConcurrently`, task aa0c3eb7).
 *
 * The 409 also carries `provisioning` — the whole status as it is NOW — so the console adopts the current
 * state from the refusal instead of re-reading it, exactly as step 4's partial 502 does and for the same
 * reason: a re-read would append a `TENANT_ACCESSED` audit row (FR-Q.35) for a crossing nobody asked for,
 * on a request that wrote nothing.
 *
 * The remedy is not a retry. The operator chose over a state that is gone, and the question they have to
 * answer again is whether their save still shuts the customer out.
 */
export const ENTITLEMENTS_CHANGED_CONCURRENTLY = "entitlements_changed_concurrently";

/**
 * Step 3 — record which applications the customer has bought. The set REPLACES the stored one, so this is
 * the whole answer and sending it twice changes nothing (the backend writes and audits only on a real
 * change).
 *
 * <b>The replace is CONDITIONAL, and `expected` is required</b> (task aa0c3eb7): it is the stored set this
 * answer was decided over, and a request whose expectation no longer holds records nothing and answers 409
 * with {@link ENTITLEMENTS_CHANGED_CONCURRENTLY}. The condition is the server's because the console cannot
 * be the one holding it — since `ApplicationEntitlementGate` this call is what shuts a customer out, and the
 * save that does it unasked is precisely the one made from a state somebody else has already changed.
 *
 * 422 when a code is outside the vocabulary or `expected` is missing; 404 when there is no such tenant.
 */
export function setEntitlements(
  tenantId: string,
  request: SetTenantEntitlementsRequest,
  signal?: AbortSignal,
): Promise<TenantProvisioning> {
  return apiFetch<TenantProvisioning>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/provisioning/entitlements`,
    { method: "PUT", body: request, signal },
  );
}

/**
 * The problem-details `error` value step 2's SECOND 409 carries: the named account's `tenant_id` attribute
 * holds something the backend cannot read as a tenant. Mirrors
 * `TenantProvisioningErrors.IdentityTenantIdMalformed`; it is what tells this 409 apart from the
 * bound-to-another-tenant one, which carries no `error` code.
 *
 * The two want opposite things from the operator — that one is fixed by naming a different person, this
 * one only by correcting the attribute in the Keycloak console — so a console that branched on the status
 * alone would print the wrong remedy.
 */
export const IDENTITY_TENANT_ID_MALFORMED = "identity_tenant_id_malformed";

/**
 * The problem-details `error` value a resend or a cancel carries when the tenant has no first administrator
 * at all (`TenantProvisioningErrors.FirstAdminNotInvited`). A 409 and not a 404: the tenant is there and
 * was named correctly, and what refused is its state — which is the shape of a console acting on a status
 * read a moment out of date. The remedy is a reload.
 */
export const FIRST_ADMIN_NOT_INVITED = "first_admin_not_invited";

/**
 * The OTHER 409 of a resend or a cancel: the invited person has already signed in
 * (`TenantProvisioningErrors.FirstAdminSignedIn`). Its own code because it shares its status with
 * {@link FIRST_ADMIN_NOT_INVITED} and wants the opposite thing from the operator — that one is fixed by
 * reloading, this one is not fixed at all, and the pääkäyttäjyys handover is the path from there.
 */
export const FIRST_ADMIN_SIGNED_IN = "first_admin_signed_in";

/**
 * Step 2's buildable half — make an address the tenant's first administrator (`tenant_id` + the ADMIN
 * client role) and mail them.
 *
 * <b>The address is all it takes and the backend picks the path</b> (ADR-0014 §7.5.1, 6.10.2026). An
 * address the realm already knows is bound as it always was and gets CLMS's own invite mail; one the realm
 * has never heard of gets a NEW account with no credential, the same two writes, and Keycloak's activation
 * mail instead — so an unknown address is no longer the 404 it was until 6.10.2026, and the console asks no
 * "does this person exist" question the operator has no way of answering.
 *
 * Every failure is an {@link ApiError} the caller turns into its own message, and in all of them nothing was
 * bound and nobody was mailed: 404 (no such tenant — the page is out of date), 409 (the account is already
 * bound to another tenant; ADR-0013 §6.3 does not let it move — or, carrying
 * {@link IDENTITY_TENANT_ID_MALFORMED}, its `tenant_id` attribute is unreadable), 422 (a malformed
 * address), 502 (the identity provider did not complete the step). The one partial outcome is a 200 whose
 * `mail` is not {@link MAIL_SENT}: the person IS bound and was not told, and the retry is
 * {@link resendFirstAdminActivation}.
 */
export function inviteFirstAdministrator(
  tenantId: string,
  request: InviteFirstAdministratorRequest,
  signal?: AbortSignal,
): Promise<TenantFirstAdminInvite> {
  return apiFetch<TenantFirstAdminInvite>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/provisioning/first-administrator`,
    { method: "POST", body: request, signal },
  );
}

/**
 * Send the bound first administrator their invite AGAIN (ADR-0014 §7.5.4) — a fresh Keycloak activation
 * link for an account this console created, or CLMS's own invite mail for one it found and bound.
 *
 * It takes no address, and that is what makes it unable to create a second account: it acts on the account
 * the tenant already names. The 200 body is the invite's, so "the mail did not leave" is reportable here
 * too (a `mail` that is not {@link MAIL_SENT}).
 *
 * 404 when the realm no longer holds the account — erased or removed by hand since it was bound, and no
 * retry can change that. 409 carrying {@link FIRST_ADMIN_NOT_INVITED} when the tenant has no first
 * administrator, or {@link FIRST_ADMIN_SIGNED_IN} when that person has already signed in. 502 when the
 * identity provider did not complete it, and then the retry is this same call.
 */
export function resendFirstAdminActivation(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantFirstAdminInvite> {
  return apiFetch<TenantFirstAdminInvite>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/provisioning/first-administrator/resend`,
    { method: "POST", signal },
  );
}

/**
 * Cancel an invite nobody has taken up (ADR-0014 §7.5.4). Step 2 reads as pending again afterwards, so the
 * console offers its address field once more.
 *
 * <b>It is not one act but two, and the server decides which.</b> The realm account is DELETED only when
 * all three hold — this console created it, it carries no credential, and no `users` row names it — and is
 * otherwise merely unbound and left in the realm, including when the credential read itself failed. The 200
 * body's `account` says which happened (`FirstAdminCancelOutcomes`), because that is the difference the
 * person on the other end would notice.
 *
 * It reaches NO customer data and is not a tenant deletion: what it removes is the invite.
 *
 * 409 carrying {@link FIRST_ADMIN_NOT_INVITED} when there is no invite to cancel, or
 * {@link FIRST_ADMIN_SIGNED_IN} once the person has signed in — by then they own rows in the customer
 * tenant, and the pääkäyttäjyys handover is the path rather than an unbinding. A 502 means the identity
 * provider refused and NOTHING was cleared: the tenant still names the account, so the retry addresses the
 * same one instead of leaving it orphaned with ADMIN.
 */
export function cancelFirstAdminInvite(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantFirstAdminCancel> {
  return apiFetch<TenantFirstAdminCancel>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/provisioning/first-administrator`,
    { method: "DELETE", signal },
  );
}

// ── step 2b · the customer's own identity provider (ADR-0014 §7.2, §12 C-6) ────────────────────────

/** Where this tenant's federation stands, and what it was asked to be — never the client secret. */
export type TenantFederation = components["schemas"]["TenantFederationDto"];

export type SetTenantFederationRequest = components["schemas"]["SetTenantFederationRequest"];

/**
 * The problem-details `error` value the federation save and withdrawal carry on their 409
 * (`TenantProvisioningErrors.FederationChangedConcurrently`): the arrangement changed between the
 * request's read and its write, and nothing was recorded.
 *
 * Its own code because the two other 409s of this surface are about an ACCOUNT — one wants a different
 * person named, the other an attribute corrected in the Keycloak console — and this one is about the
 * tenant's arrangement and wants the form reloaded.
 */
export const FEDERATION_CHANGED_CONCURRENTLY = "federation_changed_concurrently";

/**
 * Record the customer's identity provider for this tenant.
 *
 * <b>It does not configure Keycloak, and the console has to say so.</b> ADR-0014 §7.2 keeps the realm
 * authority out of the request path: this writes the arrangement and hands the client secret to OpenBao,
 * and a separately credentialed provisioning component is what writes the realm IdP. So a successful save
 * comes back `pending_apply`, not `applied`, and nothing here should present it as finished.
 *
 * A REPLACE, like {@link setEntitlements}: the request is the whole arrangement, so re-sending it with a new
 * secret is how a federation credential is rotated — there is no separate rotate operation. The secret is
 * required on every save, including one that changes only the issuer, because this API may write it and not
 * read it back and so cannot fill in a blank.
 *
 * 404 when there is no such customer tenant (the operator's own home included), 422 for a missing field or
 * an issuer that is not an absolute https URL, 502 when the secret could not be stored — and in that last
 * case NOTHING was recorded, so the retry is this same call. 409 carrying
 * {@link FEDERATION_CHANGED_CONCURRENTLY} when the arrangement moved between this request's read and its
 * write: nothing was recorded, the secret this call had stored was deleted again, and the remedy is a
 * RELOAD rather than a retry — somebody else's arrangement is the one in place, and it may be the one that
 * should stand.
 */
export function setFederation(
  tenantId: string,
  request: SetTenantFederationRequest,
  signal?: AbortSignal,
): Promise<TenantProvisioning> {
  return apiFetch<TenantProvisioning>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/provisioning/federation`,
    { method: "PUT", body: request, signal },
  );
}

/**
 * Withdraw the customer's identity provider — the tenant's people go back to the platform realm's own
 * credentials, and the provisioning component removes the realm IdP on its next pass.
 *
 * Idempotent, including on a tenant that never had one: there is no "nothing to remove" refusal, so the
 * button is safe on a status read a moment out of date. It unlinks nobody — the people who signed in
 * through that provider keep their rows and their tenant binding; what is withdrawn is the broker.
 *
 * The one refusal is the 409 carrying {@link FEDERATION_CHANGED_CONCURRENTLY}: a DIFFERENT arrangement is
 * in place from the one this request read. Nothing is cleared and no secret is deleted, because the delete
 * names the version the arrangement this request read was saved with — deleting it would take the live
 * arrangement's credential away. Reload and withdraw the arrangement that is actually there.
 */
export function removeFederation(
  tenantId: string,
  signal?: AbortSignal,
): Promise<TenantProvisioning> {
  return apiFetch<TenantProvisioning>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/provisioning/federation`,
    { method: "DELETE", signal },
  );
}

// ── step 4 · default content from the shared library (ADR-0014 §8, §12 D-2) ────────────────────────

/** The 200 body of step 4: the whole status after the step, plus what became of each version. */
export type TenantDefaultContent = components["schemas"]["TenantDefaultContentDto"];

/** One version's outcome. */
export type TenantDefaultContentItem = components["schemas"]["TenantDefaultContentItemDto"];

export type ApplyDefaultContentRequest = components["schemas"]["ApplyDefaultContentRequest"];

/** One published version of a library item, as the platform catalogue lists it. */
export type ContentLibraryItem = components["schemas"]["ContentLibraryItemDto"];

/**
 * The `outcome` of a version this request COPIED, as opposed to one the tenant already had
 * (`already_applied`). Named because the wizard's success line branches on it: an operator who is told only
 * "done" cannot tell a retry that did nothing from a first run that did everything.
 */
export const CONTENT_APPLIED = "applied";

/**
 * The problem-details `error` value step 4's SECOND 502 carries (`TenantProvisioningErrors.ContentCopyUnknown`):
 * the copy stopped at a version whose outcome could not be established — the apply threw and whether its
 * transaction committed could not be read back.
 *
 * It is what tells that 502 from the ordinary one, which carries `content_copy_failed` and means the version
 * it stopped at is NOT in the tenant. Only this code is named here because only it changes what the console
 * says: "try that version again" is the whole remedy for the first, while here the retry is safe but the
 * assumption underneath it — that nothing landed — is one nobody has checked. A 502 with any other code, or
 * with none, is the ordinary partial stop.
 */
export const CONTENT_COPY_UNKNOWN = "content_copy_unknown";

/**
 * Every published library version, newest first — the catalogue step 4 picks from. The same route the
 * publish surface lists, because there is one library and no second read of it.
 */
export function listContentLibrary(signal?: AbortSignal): Promise<ContentLibraryItem[]> {
  return apiFetch<ContentLibraryItem[]>("/api/platform/content-library", { signal });
}

/**
 * The newest published version of each item, by `itemKey` — what step 4 OFFERS, and only that. The grouping
 * is here and not on the server because the route above is the PUBLISH surface's list, which must keep
 * showing every version; this mirrors `ContentLibraryService.ListAsync`'s own newest-per-key pick, which is
 * what the tenant's catalogue applies.
 *
 * Narrow where you offer, never where you RESOLVE: a tenant may hold a version this function drops, because
 * versions accumulate (§8) and a copy cannot be taken back. The step resolves its applied ids against the
 * unnarrowed response, or a superseded copy would read as one the library no longer has.
 */
export function newestPerItem(items: readonly ContentLibraryItem[]): ContentLibraryItem[] {
  const newest = new Map<string, ContentLibraryItem>();
  for (const item of items) {
    const held = newest.get(item.itemKey);
    if (!held || item.version > held.version) newest.set(item.itemKey, item);
  }

  return [...newest.values()].sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Step 4 — copy the named library versions into the tenant as its own courses.
 *
 * An ADD and not a replace: an applied item is the tenant's own course the moment it lands and nothing here
 * can take it back, so the request names what to copy rather than what the answer should become. Sending
 * the same versions again copies nothing and reports them `already_applied`.
 *
 * 422 when the request names no version, 404 when it names one the library does not have (and then NOTHING
 * is copied — the ids are checked as a set first). A 502 is the partial outcome: a copy stopped partway, the
 * versions before it ARE in the tenant, and the problem body carries `provisioning` and `items` so the
 * console can show what landed. The retry is this same call.
 *
 * The 502's `error` tells its two endings apart — `content_copy_failed` when the version it stopped at is
 * known not to be in the tenant, {@link CONTENT_COPY_UNKNOWN} when that could not be established.
 */
export function applyDefaultContent(
  tenantId: string,
  request: ApplyDefaultContentRequest,
  signal?: AbortSignal,
): Promise<TenantDefaultContent> {
  return apiFetch<TenantDefaultContent>(
    `/api/platform/tenants/${encodeURIComponent(tenantId)}/provisioning/default-content`,
    { method: "POST", body: request, signal },
  );
}
